import type { BadgeAddOn, BadgeColor, BadgeSize } from '@unifyapps/defs/blocks/Icon/types';
import { Box } from '@unifyapps/ui/components/Box';
import { Pill } from '@unifyapps/ui/components/Pill';
import type { PillColors } from '@unifyapps/ui/components/Pill/types';
import { clsx } from 'clsx';
import { memo } from 'react';

export const ICON_BADGE_ID_CLASSNAME = 'ua-icon-badge';

// user-facing sizes (sm/md/lg) → Pill's internal sizes (xs/sm/md)
// Pill tops out at `md`, so `lg` maps to the largest available size.
const PILL_SIZE_MAP: Record<BadgeSize, 'xs' | 'sm' | 'md'> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
};

// `info` → `blue` because PillColors doesn't have a dedicated info token.
const PILL_COLOR_MAP: Record<BadgeColor, PillColors> = {
  error: 'error',
  success: 'success',
  warning: 'warning',
  info: 'gray',
};

const getLabelTypography = (size: BadgeSize) => {
  switch (size) {
    case 'sm':
      return { variant: 'text-xxxs' as const, weight: 'medium' as const };
    case 'md':
      return { variant: 'text-xxxs' as const, weight: 'medium' as const };
    default:
      return { variant: 'text-xxs' as const, weight: 'medium' as const };
  }
};

const getPillClassName = (size: BadgeSize, type: 'text' | 'dot') => {
  if (type === 'dot') {
    switch (size) {
      case 'sm':
        return '!py-0 !px-0 !h-2 !w-2';
      case 'md':
        return '!py-0 !px-0 !h-3 !w-3';
      default:
        return '!py-0 !px-0 !h-4 !w-4';
    }
  }

  switch (size) {
    case 'sm':
      return '!px-xxs !py-xxs !h-4';
    case 'md':
      return '!px-xs !py-xxs !h-4';
    default:
      return '!px-sm !py-xs !h-5';
  }
};

function IconBadge({
  badge,
  iconSize,
  variant,
  customVariantDataAttrs,
}: {
  badge: BadgeAddOn;
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'transparent' | 'featured';
  customVariantDataAttrs?: Record<string, string>;
}) {
  const { size = 'sm', color = 'error', type = 'text', text } = badge;

  // nothing to render for empty text badges — avoids a bare pill
  if (type === 'text' && !text) return null;

  // class + data-color on Pill (not wrapper) so theme CSS lands on the visible element
  return (
    <Box className="absolute z-10 top-0 end-0">
      <Pill
        className={clsx(ICON_BADGE_ID_CLASSNAME, getPillClassName(size, type))}
        color={PILL_COLOR_MAP[color]}
        data-badgeColor={color}
        // overrides Pill's data-size so theme-manager can scope badge offsets by iconSize.
        // if Pill's pill-size data-size is ever needed too, move this to data-icon-size.
        data-size={iconSize}
        dotClassName="!h-0 !w-0"
        isDot={type === 'dot'}
        label={type === 'text' ? text : undefined}
        // inherit color from Pill root so theme CSS overrides Pill's `text-white`.
        // `!important` is required: Pill's labelVariants sets `text-white` on the same
        // element and both classes are merged via clsx (not twMerge), so without the
        // `!` the cascade order is undefined and `text-white` usually wins.
        labelClassName="!text-inherit"
        labelTypography={getLabelTypography(size)}
        shape="rounded"
        size={PILL_SIZE_MAP[size]}
        variant="solid"
        {...customVariantDataAttrs}
      />
    </Box>
  );
}

export default memo(IconBadge);
